import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Plot from 'react-plotly.js'; // Import Plotly component
import * as d3 from 'd3';
const apiUrl = process.env.REACT_APP_API_URL;

function ProteinDetail() {
    const { id } = useParams(); // Get proteinId from URL
    const [protein, setProtein] = useState(null); // State to store protein data
    const [loading, setLoading] = useState(true); // State to manage loading status
    const [error, setError] = useState(null); // State to handle errors
    const [networkPlotData, setNetworkPlotData] = useState(null); // State for network plot data
    const [parsedNetworkPlot, setParsedNetworkPlot] = useState(null); // State for parsed network plot
    const [activeTab, setActiveTab] = useState('heatmap'); // State to track active tab
    const [showSecondaryNodes, setShowSecondaryNodes] = useState(true); // State for secondary nodes visibility

    // Function to fetch protein data
    const fetchProtein = async (id) => {
        try {
            const response = await fetch(`${apiUrl}/api/proteins/${id}/`); // Backend endpoint
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            console.log("fetched protein data:", data);
            setProtein(data); // Set the fetched protein data

            // Parse network JSON data if it exists
            if (data.network_json) {
                try {
                    const networkData = typeof data.network_json === 'string'
                        ? JSON.parse(data.network_json)
                        : data.network_json;
                    setNetworkPlotData(networkData);

                    // If the data is already in Plotly format
                    if (networkData.data && networkData.layout) {
                        setParsedNetworkPlot(networkData);
                    } else {
                        // Otherwise parse it from the format in the rf_2D_network function
                        parseNetworkData(networkData);
                    }
                } catch (e) {
                    console.error('Failed to parse network_json:', e);
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false); // Stop loading
        }
    };

    // Function to parse network data from backend format to Plotly format
    const parseNetworkData = (networkData) => {
        try {
            // Create primary edge trace
            const primaryEdgeTrace = {
                x: [],
                y: [],
                line: { width: 2, color: '#10a6e6' },
                hoverinfo: 'none',
                mode: 'lines',
                name: 'Primary Connections',
                showlegend: false
            };

            // Create secondary edge trace
            const secondaryEdgeTrace = {
                x: [],
                y: [],
                line: { width: 2, color: '#a1a4a6' },
                hoverinfo: 'none',
                mode: 'lines',
                name: 'Secondary Connections',
                customdata: [],
                showlegend: false
            };

            // Add edges
            for (const edgeId in networkData.edges) {
                const edge = networkData.edges[edgeId];
                const sourcePos = networkData.positions[edge.source];
                const targetPos = networkData.positions[edge.target];

                if (edge.is_primary) {
                    primaryEdgeTrace.x.push(sourcePos.x, targetPos.x, null);
                    primaryEdgeTrace.y.push(sourcePos.y, targetPos.y, null);
                } else {
                    secondaryEdgeTrace.x.push(sourcePos.x, targetPos.x, null);
                    secondaryEdgeTrace.y.push(sourcePos.y, targetPos.y, null);
                    secondaryEdgeTrace.customdata.push(edgeId, edgeId, edgeId);
                }
            }

            // Create node traces
            const primaryNodeX = [], primaryNodeY = [];
            const primaryNodeColors = [], primaryNodeText = [];
            const secondaryNodeX = [], secondaryNodeY = [];
            const secondaryNodeColors = [], secondaryNodeText = [];

            // Determine the POI from the title
            const poiMatch = networkData.title.match(/Machine Learning Network Plot/);
            const poi = poiMatch ? networkData.title.split(' ').pop().replace(/[()]/g, '') : '';

            for (const node in networkData.nodes) {
                const nodeData = networkData.nodes[node];
                const position = networkData.positions[node];

                if (node === poi || nodeData.is_primary) {
                    primaryNodeX.push(position.x);
                    primaryNodeY.push(position.y);
                    primaryNodeColors.push(nodeData.color);
                    primaryNodeText.push(nodeData.label);
                } else {
                    secondaryNodeX.push(position.x);
                    secondaryNodeY.push(position.y);
                    secondaryNodeColors.push(nodeData.color);
                    secondaryNodeText.push(nodeData.label);
                }
            }

            // Primary node trace
            const primaryNodeTrace = {
                x: primaryNodeX,
                y: primaryNodeY,
                text: primaryNodeText,
                mode: 'markers+text',
                hoverinfo: 'text',
                marker: {
                    color: primaryNodeColors,
                    size: 15,
                    line: { width: 2 }
                },
                textposition: "top center",
                textfont: {
                    family: networkData.styling?.font?.family || 'sans-serif',
                    size: networkData.styling?.font?.size || 10,
                    color: networkData.styling?.font?.color || 'black'
                },
                texttemplate: '<span style="background-color: white; padding: 2px; border-radius: 3px">%{text}</span>',
                name: 'Primary Nodes',
                showlegend: false
            };

            // Secondary node trace
            const secondaryNodeTrace = {
                x: secondaryNodeX,
                y: secondaryNodeY,
                text: secondaryNodeText,
                mode: 'markers+text',
                hoverinfo: 'text',
                marker: {
                    color: secondaryNodeColors,
                    size: 15,
                    line: { width: 2 }
                },
                textposition: "top center",
                textfont: {
                    family: networkData.styling?.font?.family || 'sans-serif',
                    size: networkData.styling?.font?.size || 10,
                    color: networkData.styling?.font?.color || 'black'
                },
                texttemplate: '<span style="background-color: white; padding: 2px; border-radius: 3px">%{text}</span>',
                name: 'Secondary Nodes',
                showlegend: false
            };

            // Combine all traces
            const data = [primaryEdgeTrace, secondaryEdgeTrace, primaryNodeTrace, secondaryNodeTrace];

            // Add legend traces
            networkData.legend.forEach(item => {
                data.push({
                    x: [null],
                    y: [null],
                    mode: 'markers',
                    marker: { size: 10, color: item.color },
                    name: item.label,
                    showlegend: true
                });
            });

            // Create layout
            const layout = {
                title: networkData.title,
                showlegend: true,
                margin: { b: 20, l: 5, r: 5, t: 40 },
                xaxis: { showgrid: false, zeroline: false, showticklabels: false },
                yaxis: { showgrid: false, zeroline: false, showticklabels: false },
                plot_bgcolor: 'white',
                updatemenus: [{
                    type: 'buttons',
                    direction: 'down',
                    showactive: true,
                    buttons: [
                        {
                            label: 'Show Secondary Nodes',
                            method: 'update',
                            args: [{ visible: [true, true, true, true, true, true, true, true] }]
                        },
                        {
                            label: 'Hide Secondary Nodes',
                            method: 'update',
                            args: [{ visible: [true, false, true, false, true, true, true, true] }]
                        }
                    ],
                    x: 0.95,
                    y: 0.01,
                    xanchor: 'right',
                    yanchor: 'bottom',
                    pad: { t: 60 },
                    active: 0
                }]
            };

            // Set the parsed network plot
            setParsedNetworkPlot({ data, layout, config: { displayModeBar: true } });
        } catch (e) {
            console.error('Failed to parse network data:', e);
        }
    };

    useEffect(() => {
        fetchProtein(id); // Fetch protein data when component mounts
    }, [id]);

    useEffect(() => {
        // Only create the circle plot when the circle plot tab is active
        if (protein && protein.circleplot_data && activeTab === 'circleplot') {
            d3.select("#circle-plot").selectAll("*").remove();

            // Set up dimensions
            const width = 600;
            const height = 600;
            const margin = 60;

            // Create SVG
            const svg = d3.select("#circle-plot")
                .append("svg")
                .attr("width", width)
                .attr("height", height);

            // Create a group and center it
            const g = svg.append("g")
                .attr("transform", `translate(${width/2}, ${height/2})`);

            // Calculate scale to fit all circles
            const maxRadius = d3.max(protein.circleplot_data, d => d.r);
            const maxDistance = d3.max(protein.circleplot_data, d =>
                Math.sqrt(d.x * d.x + d.y * d.y) + d.r
            );

            // Scale to fit everything within the SVG with some padding
            const scale = (Math.min(width, height) - 2 * margin) / (2 * maxDistance);

            // Create circles (draw largest circles first)
            const circles = g.selectAll("circle")
                .data(protein.circleplot_data.sort((a, b) => b.r - a.r)) // Sort by radius descending
                .enter()
                .append("circle")
                .attr("cx", d => d.x * scale)
                .attr("cy", d => d.y * scale)
                .attr("r", d => d.r * scale)
                .style("fill", d => d.color)
                .style("opacity", 0.7)
                .style("stroke", "black")
                .style("stroke-width", 1);

            // Add hover effects
            circles
                .on("mouseover", function(event, d) {
                    d3.select(this)
                        .style("stroke", "orange")
                        .style("stroke-width", 2)
                        .style("opacity", 1);

                    // Add tooltip
                    g.append("text")
                        .attr("class", "tooltip")
                        .attr("x", d.x * scale)
                        .attr("y", d.y * scale - (d.r * scale) - 10)
                        .attr("text-anchor", "middle")
                        .style("font-size", "12px")
                        .text(`${d.name} (r: ${d.r.toFixed(1)})`);
                })
                .on("mouseout", function() {
                    d3.select(this)
                        .style("stroke", "black")
                        .style("stroke-width", 1)
                        .style("opacity", 0.7);

                    g.selectAll(".tooltip").remove();
                });
        }
    }, [protein, activeTab]);

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    // Check if graph_html is a string and parse it
    let plotData = null;
    if (protein.graph_html) {
        try {
            plotData = typeof protein.graph_html === 'string' ? JSON.parse(protein.graph_html) : protein.graph_html;
        } catch (e) {
            console.error('Failed to parse graph_html:', e);
        }
    }

    // Function to handle tab switching
    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    // Event handler for Plotly checkbox events
    const handlePlotlyEvent = (eventData) => {
        if (eventData && eventData.event === 'plotly_update') {
            // The first button in updatemenus is our secondary nodes checkbox
            // If 'visible' includes 4 true values, all traces are visible
            // If 'visible' includes 2 false values, secondary traces are hidden
            const visibilityArray = eventData.update?.visible;
            if (visibilityArray) {
                const allVisible = !visibilityArray.includes(false);
                setShowSecondaryNodes(allVisible);
            }
        }
    };

    return (
        <div className="protein-detail mt-8 pt-20 mb-20">
            <h1 className="text-5xl font-bold mb-4">{protein.gene_name}</h1>
            <div className="flex justify-center">
                <table className="w-3/4 border-collapse mt-4">
                    <tbody>
                    <tr>
                        <td className="border px-4 py-2 font-bold bg-gray-100 w-1/3">UniProt ID</td>
                        <td className="border px-4 py-2">{protein.uniprot_id}</td>
                    </tr>
                    <tr>
                        <td className="border px-4 py-2 font-bold bg-gray-100">Graph Connectivity</td>
                        <td className="border px-4 py-2">{protein.surface_coverage}</td>
                    </tr>
                    <tr>
                        <td className="border px-4 py-2 font-bold bg-gray-100">Annotation</td>
                        <td className="border px-4 py-2">{protein.annotation}</td>
                    </tr>
                    <tr>
                        <td className="border px-4 py-2 font-bold bg-gray-100">Cell Line</td>
                        <td className="border px-4 py-2">{protein.cell_lines}</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            {/* Tab Navigation */}
            <div className="tabs-container">
                <div className="tab-navigation flex justify-center border-b pt-6">
                    <button
                        className={`px-4 py-2 mx-2 ${activeTab === 'heatmap' 
                            ? 'bg-blue-500 text-[#9b4819] border-b-2 border-[#9b4819]' 
                            : 'bg-gray-200'} rounded-t-lg`}
                        onClick={() => handleTabChange('heatmap')}
                    >
                        3D Network Heatmap
                    </button>
                    <button
                        className={`px-4 py-2 mx-2 ${activeTab === 'network' 
                            ? 'bg-blue-500 text-[#9b4819] border-b-2 border-[#9b4819]' 
                            : 'bg-gray-200'} rounded-t-lg`}
                        onClick={() => handleTabChange('network')}
                    >
                        2D Network Analysis
                    </button>
                    <button
                        className={`px-4 py-2 mx-2 ${activeTab === 'circleplot' 
                            ? 'bg-blue-500 text-[#9b4819] border-b-2 border-[#9b4819]' 
                            : 'bg-gray-200'} rounded-t-lg`}
                        onClick={() => handleTabChange('circleplot')}
                    >
                        Clustered Surface Proteome
                    </button>
                </div>

                {/* Tab Content */}
                <div className="tab-content mt-4">
                    {/* Heatmap Tab Content */}
                    {activeTab === 'heatmap' && plotData && (
                        <div className="plot-container w-full flex justify-center">
                            <div className="max-w-5xl">
                                <Plot
                                    data={plotData.data}
                                    layout={plotData.layout}
                                    config={plotData.config}
                                    style={{width: '800px', height: '600px'}}
                                />
                            </div>
                        </div>
                    )}

                    {/* Network Plot Tab Content */}
                    {activeTab === 'network' && parsedNetworkPlot && (
                        <div className="network-plot-container w-full flex justify-center">
                            <div className="max-w-5xl">
                                <Plot
                                    data={parsedNetworkPlot.data}
                                    layout={parsedNetworkPlot.layout}
                                    config={parsedNetworkPlot.config}
                                    style={{width: '800px', height: '600px'}}
                                    onUpdate={handlePlotlyEvent}
                                />
                            </div>
                        </div>
                    )}

                    {/* Circle Plot Tab Content */}
                    {activeTab === 'circleplot' && protein && protein.circleplot_data && (
                        <div id="circle-plot-container" className="w-full flex flex-col items-center">
                            <div id="circle-plot" className="w-[600px] h-[600px]"></div>

                            {/* Legend Image */}
                            <div className="legend-container text-center mt-4">
                                <img
                                    src="/legend.png" // Path to your legend image
                                    alt="Circle Plot Legend"
                                    style={{width: '400px', height: 'auto'}}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProteinDetail;